import { useState } from "react";
import { MapPin, Clock, Ticket, Baby, ShieldAlert, ExternalLink } from "lucide-react";
import { useApi, ago } from "../lib/api";
import { PublicHeader, Footer } from "../components/PublicChrome";
import { BookingForm } from "../components/public/Museum";
import { Eyebrow } from "./Home";

const M = "/brand/museum/";
const SITE = "https://www.despiteautomuseum.com";
const CARS = [
  ["lamborghini-revuelto", "Lamborghini Revuelto", "A new era of the V12", "Uniting the emotion of an iconic V12 with a new generation of electrified performance — a new chapter in supercar history.", "revuelto.webp"],
  ["new-page", "Bugatti Type 35", "A racing legend", "Lightweight, precise and record-breaking — it dominated the Grand Prix circuits of the 1920s and 1930s with its horseshoe grille and striking blue finish.", "bugatti-type-35.webp"],
  ["new-page-1", "BMW 501", "The 'Baroque Angel'", "BMW's first major post-war success: flowing curves, the signature kidney grille and a plush interior favoured by elites and dignitaries.", "bmw-501.webp"],
  ["new-page-2", "Excalibur Series III", "A timeless classic", "Inspired by 1930s Mercedes-Benz roadsters, hand-crafted in the 1970s with a powerful V8 and regal chrome detailing.", "excalibur.webp"],
  ["new-page-3", "1962 Chevrolet Corvette", "The American dream on wheels", "The last of the first-generation C1 Corvettes — a 327 cubic-inch V8 producing up to 360 horsepower.", "corvette.webp"],
  ["new-page-3-1", "1932 Ford Coupe", "The iconic hot rod", "The legendary 'Deuce Coupe' — bold yellow paint, sweeping fenders and a vertical-bar grille that defined American car culture.", "ford-1932.webp"],
  ["new-page-3-1-1", "Porsche 356 Speedster", "The essence of open-air driving", "A lightweight, low-slung 1950s convertible in deep burgundy with chrome accents and white-wall tyres.", "porsche-356.webp"],
  ["1957-buick-roadmaster-history", "1957 Buick Roadmaster", "Jet-age Americana", "Dramatic chrome, sweeping side panels and signature 'VentiPorts' — added to Dr. Despite's collection as a celebration of a golden age.", "buick.jpg"],
];
const FACTS = [
  [MapPin, "Location", "200 Cotton Street, East Legon, Accra · Digital address JRVX+84P"],
  [Clock, "Opening hours", "Monday – Sunday, 10:00 am – 8:00 pm, in hourly sessions. Sessions are limited — RSVP advised."],
  [Ticket, "Admission", "GHS 200 per person. Confirm and pay to reserve; present your RSVP confirmation at reception."],
  [Baby, "Children under 12", "Book the children's tour separately."],
];
const RULES = ["No photography inside exhibit areas", "No food or drinks in exhibit spaces", "Do not touch or lean on the vehicles", "Follow posted signs and staff instructions"];

const Hero = () => (
  <section className="relative overflow-hidden bg-[#0e0b04] text-white" data-testid="museum-hero">
    <img src={`${M}showcase.gif`} alt="Despite Automobile Museum showcase" className="absolute inset-0 w-full h-full object-cover opacity-45" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0e0b04] via-[#0e0b04]/70 to-transparent" />
    <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
      <Eyebrow light>Despite Automobile Museum · East Legon, Accra</Eyebrow>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold max-w-3xl leading-[1.05]">Rare collections. Family moments. <span className="gold-text">Iconic rides.</span></h1>
      <p className="mt-6 max-w-xl text-white/85 text-base md:text-lg">Ghana's first-ever auto museum — the private collection of Dr. Osei Kwame Despite, shown to the public for the first time. 85+ remarkable automobiles across generations.</p>
      <div className="mt-8 flex flex-wrap gap-3">
        <a href="#book" className="btn-gold !bg-[color:var(--gold-hi)] !text-[#064D29]" data-testid="museum-hero-book">Plan your visit</a>
        <a href="#collection" className="btn-ghost" data-testid="museum-hero-collection">See the collection</a>
      </div>
    </div>
  </section>
);

const Collection = () => (
  <section id="collection" className="max-w-7xl mx-auto px-5 md:px-8 py-16" data-testid="museum-collection">
    <Eyebrow>Spotlight collection</Eyebrow>
    <h2 className="font-display text-3xl md:text-4xl font-bold text-ink max-w-2xl">Cars you've only seen in films and magazines.</h2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
      {CARS.map(([slug, n, tag, d, img]) => (
        <a key={slug} href={`${SITE}/${slug}`} target="_blank" rel="noreferrer" className="panel panel-hover overflow-hidden group flex flex-col" data-testid={`museum-car-${slug}`}>
          <div className="h-48 bg-[#0e0b04] overflow-hidden relative">
            {img ? <img src={M + img} alt={n} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              : <div className="w-full h-full bg-[#0e0b04]" />}
            {slug.startsWith("1957") && <span className="absolute bottom-2 right-2 font-data text-[9px] uppercase tracking-wider text-white/70 bg-black/50 px-1.5 py-0.5 rounded">Representative photo</span>}
          </div>
          <div className="p-5 flex-1">
            <div className="font-data text-[10px] uppercase tracking-widest text-[color:var(--gold)]">{tag}</div>
            <div className="font-display text-lg text-ink font-bold mt-1">{n}</div>
            <p className="text-xs text-ink-3 mt-2 leading-relaxed">{d}</p>
          </div>
        </a>
      ))}
    </div>
  </section>
);

const Opening = () => (
  <section className="bg-brand-deep text-white" data-testid="museum-opening">
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-16">
      <Eyebrow light>Grand opening</Eyebrow>
      <h2 className="font-display text-3xl md:text-4xl font-bold max-w-2xl">Commissioned by His Majesty Otumfuo Osei Tutu II.</h2>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-10">
        {[1, 2, 3, 4, 5].map((i) => <img key={i} src={`${M}opening-${i}.webp`} alt={`Grand opening ${i}`} loading="lazy" className={`w-full h-44 md:h-56 object-cover rounded gold-frame-sm ${i === 1 ? "col-span-2 md:col-span-1" : ""}`} data-testid={`museum-opening-img-${i}`} />)}
      </div>
    </div>
  </section>
);

const Visit = () => {
  const [ref, setRef] = useState(null);
  return (
    <section id="book" className="max-w-7xl mx-auto px-5 md:px-8 py-16 grid lg:grid-cols-2 gap-10" data-testid="museum-visit">
      <div>
        <Eyebrow>Plan your visit</Eyebrow>
        <h2 className="font-display text-3xl md:text-4xl font-bold text-ink">Welcome to Ghana's home of automotive legacy.</h2>
        <div className="mt-8 space-y-4">
          {FACTS.map(([I, t, d]) => (
            <div key={t} className="flex gap-4" data-testid={`museum-fact-${t.toLowerCase().replace(/\W+/g, "-")}`}>
              <span className="w-10 h-10 rounded-full bg-[#F4F8F5] flex items-center justify-center shrink-0"><I size={16} className="text-brand-green" /></span>
              <div><div className="text-sm font-semibold text-ink">{t}</div><div className="text-sm text-ink-3">{d}</div></div>
            </div>
          ))}
        </div>
        <div className="panel p-5 mt-8">
          <div className="flex items-center gap-2 mb-3"><ShieldAlert size={15} className="text-brand-green" /><span className="eyebrow">Visitor guidelines</span></div>
          <ul className="text-sm text-ink-3 space-y-1 list-disc pl-5">{RULES.map((r) => <li key={r}>{r}</li>)}</ul>
        </div>
        <div className="flex flex-wrap gap-2 mt-6">
          <a href={`${SITE}/plan-your-visit`} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="museum-official-pay">Confirm & pay on official site <ExternalLink size={13} /></a>
          <a href={`${SITE}/children-under-twelve-tour`} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="museum-kids-tour">Children's tour <ExternalLink size={13} /></a>
        </div>
      </div>
      <div className="panel p-6 h-fit">
        <div className="eyebrow mb-4">Request a visit</div>
        <BookingForm onDone={setRef} />
        {ref && <p className="mt-3 font-data text-xs text-brand-green" data-testid="museum-booking-ref">Reference: {ref}</p>}
      </div>
    </section>
  );
};

const Vision = () => (
  <section className="bg-[#F4F8F5] border-y border-[color:var(--line)]" data-testid="museum-vision">
    <div className="max-w-4xl mx-auto px-5 md:px-8 py-16">
      <Eyebrow>More than a museum · A mission in motion</Eyebrow>
      <h2 className="font-display text-3xl md:text-4xl font-bold text-ink">Driven by a dream.</h2>
      <p className="text-ink-3 mt-5 leading-relaxed">The museum was born from the vision of Dr. Osei Kwame Despite, whose love for cars began as a boy. What started as admiration for beautiful machines became a lifelong collection — never shown to the public, until now. Its mission: an unforgettable automotive experience that blends education, admiration and Ghanaian pride, inspiring future generations while celebrating engineering, design and determination.</p>
      <a href={`${SITE}/blog-2-1/the-vision-behind-the-museum`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-brand-green font-semibold mt-5 hover:underline" data-testid="museum-vision-link">Read "The Vision Behind the Museum" <ExternalLink size={13} /></a>
    </div>
  </section>
);

const MotoringNews = () => {
  const { data } = useApi("/public/motoring-news");
  const items = data?.items || [];
  return (
    <section className="max-w-7xl mx-auto px-5 md:px-8 py-16" data-testid="museum-motoring-news">
      <Eyebrow>Daily motoring news · from around the world</Eyebrow>
      <h2 className="font-display text-3xl md:text-4xl font-bold text-ink">Today on the road.</h2>
      {!data && <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">{[0, 1, 2].map((i) => <div key={i} className="panel h-72 animate-pulse" />)}</div>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
        {items.slice(0, 9).map((n, i) => (
          <a key={n.id} href={n.link} target="_blank" rel="noreferrer" className="panel panel-hover overflow-hidden group flex flex-col" data-testid={`motoring-news-${i}`}>
            <div className="h-44 bg-[#0e0b04] overflow-hidden">{n.image ? <img src={n.image} alt="" loading="lazy" referrerPolicy="no-referrer" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /> : <img src={`${M}corvette.webp`} alt="" className="w-full h-full object-cover opacity-60" />}</div>
            <div className="p-5 flex-1">
              <div className="font-data text-[10px] uppercase tracking-widest text-[color:var(--gold)]">{n.source}{n.published && ` · ${ago(n.published)}`}</div>
              <div className="font-display text-base text-ink font-bold mt-1 leading-snug">{n.title}</div>
              <p className="text-xs text-ink-3 mt-2 line-clamp-3">{n.summary}</p>
            </div>
          </a>
        ))}
      </div>
      {data && <p className="font-data text-[10px] uppercase tracking-wider text-ink-3 mt-4">Sources: {data.sources.join(", ")} · refreshed every 6 hours</p>}
    </section>
  );
};

export default function MuseumPage() {
  return (
    <div data-testid="museum-page">
      <PublicHeader />
      <Hero />
      <Collection />
      <Opening />
      <Visit />
      <Vision />
      <MotoringNews />
      <p className="max-w-7xl mx-auto px-5 md:px-8 font-data text-[10px] uppercase tracking-wider text-ink-3">Content & photography: despiteautomuseum.com</p>
      <Footer />
    </div>
  );
}
